/* Bottom rail — the clock ticker, the message rotation, the Ami hint and the Ami hand-off.
   Markup and styling: sections/bottom-rail.liquid.

   The ticker (clock and messages) is desktop only: below 1000px the section does not render its capsule and the
   Ami capsule takes the row, so the compact branches here only stop the rotation and turn a tap on the capsule
   into an open (Sep 2026).

   The clock line cycles (Sep 22 2026, Anthony): it rests on "It's always 2am somewhere", then goes through every
   city where it is 2am right now — "It's 2:14:07am in Lisbon" — one per rotation step, and comes back to the
   phrase after the last city. The phrase is the section's clock_fallback setting, so it is also what the line
   says before this script lands. cityIndex 0 is the phrase; city n is cityIndex n + 1.

   The clock answers "where is it 2am right now". Every instant of the day falls in some UTC offset's 2 o'clock
   hour, so the question always has an answer; the list below is a curated set of ~70 cities chosen so that every
   quarter-hour of the day has at least one named place in the band, checked against both a summer and a winter
   date (offsets run -12 to +14, and the quarter-hour zones — Kathmandu +5:45, Chatham +12:45, Marquesas -9:30 —
   shift the set at :30 and :45 past the hour). The thin hours are the good ones: for about an hour a day the only
   place on earth at 2am is Gambier.

   Intl does all the work — DST, half-hour and quarter-hour offsets, and the abolition of a zone's DST in some
   future year are the browser's problem, not a table we have to maintain. */

(() => {
  'use strict';

  /* [display name, IANA zone]. Display names are what the ticker says, so they are the familiar form of the
     city rather than the zone id ("Buenos Aires", not "America/Argentina/Buenos_Aires"). */
  const CITIES = [
    ['Auckland', 'Pacific/Auckland'],
    ['Suva', 'Pacific/Fiji'],
    ['the Chatham Islands', 'Pacific/Chatham'],
    ['Norfolk Island', 'Pacific/Norfolk'],
    ['Sydney', 'Australia/Sydney'],
    ['Brisbane', 'Australia/Brisbane'],
    ['Adelaide', 'Australia/Adelaide'],
    ['Darwin', 'Australia/Darwin'],
    ['Perth', 'Australia/Perth'],
    ['Tokyo', 'Asia/Tokyo'],
    ['Seoul', 'Asia/Seoul'],
    ['Shanghai', 'Asia/Shanghai'],
    ['Hong Kong', 'Asia/Hong_Kong'],
    ['Singapore', 'Asia/Singapore'],
    ['Manila', 'Asia/Manila'],
    ['Jakarta', 'Asia/Jakarta'],
    ['Bangkok', 'Asia/Bangkok'],
    ['Ho Chi Minh City', 'Asia/Ho_Chi_Minh'],
    ['Yangon', 'Asia/Yangon'],
    ['Dhaka', 'Asia/Dhaka'],
    ['Kathmandu', 'Asia/Kathmandu'],
    ['Kolkata', 'Asia/Kolkata'],
    ['Mumbai', 'Asia/Kolkata'],
    ['Karachi', 'Asia/Karachi'],
    ['Tashkent', 'Asia/Tashkent'],
    ['Kabul', 'Asia/Kabul'],
    ['Dubai', 'Asia/Dubai'],
    ['Tehran', 'Asia/Tehran'],
    ['Moscow', 'Europe/Moscow'],
    ['Riyadh', 'Asia/Riyadh'],
    ['Nairobi', 'Africa/Nairobi'],
    ['Istanbul', 'Europe/Istanbul'],
    ['Athens', 'Europe/Athens'],
    ['Cairo', 'Africa/Cairo'],
    ['Johannesburg', 'Africa/Johannesburg'],
    ['Paris', 'Europe/Paris'],
    ['Berlin', 'Europe/Berlin'],
    ['Madrid', 'Europe/Madrid'],
    ['Rome', 'Europe/Rome'],
    ['Amsterdam', 'Europe/Amsterdam'],
    ['Stockholm', 'Europe/Stockholm'],
    ['Lagos', 'Africa/Lagos'],
    ['London', 'Europe/London'],
    ['Lisbon', 'Europe/Lisbon'],
    ['Dublin', 'Europe/Dublin'],
    ['Reykjavik', 'Atlantic/Reykjavik'],
    ['Accra', 'Africa/Accra'],
    ['Praia', 'Atlantic/Cape_Verde'],
    ['the Azores', 'Atlantic/Azores'],
    ['Fernando de Noronha', 'America/Noronha'],
    ['São Paulo', 'America/Sao_Paulo'],
    ['Buenos Aires', 'America/Argentina/Buenos_Aires'],
    ['Santiago', 'America/Santiago'],
    ['Halifax', 'America/Halifax'],
    ['New York', 'America/New_York'],
    ['Toronto', 'America/Toronto'],
    ['Bogotá', 'America/Bogota'],
    ['Lima', 'America/Lima'],
    ['Mexico City', 'America/Mexico_City'],
    ['Chicago', 'America/Chicago'],
    ['Denver', 'America/Denver'],
    ['Phoenix', 'America/Phoenix'],
    ['Los Angeles', 'America/Los_Angeles'],
    ['Vancouver', 'America/Vancouver'],
    ['Anchorage', 'America/Anchorage'],
    ['Honolulu', 'Pacific/Honolulu'],
    ['the Marquesas', 'Pacific/Marquesas'],
    ['Gambier', 'Pacific/Gambier'],
    ['Kiritimati', 'Pacific/Kiritimati'],
    ['Pago Pago', 'Pacific/Pago_Pago'],
    ['Apia', 'Pacific/Apia'],
  ];

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const COMPACT = window.matchMedia('(max-width: 999px)');

  function track(event, props) {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(event, props);
      }
    } catch {
      /* analytics never break the rail */
    }
  }

  /* Must match the transition duration on .bottom-rail__roll-line in sections/bottom-rail.liquid, plus a frame. */
  const ROLL_MS = 400;
  const ROLL_SETTLE_MS = ROLL_MS + 50;

  /* Local time in a zone. Intl is the only correct way to do this: a fixed offset table would be wrong for half
     the year in half the world. en-GB with hour12 false gives 00-23. */
  function localTime(zone, date) {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: zone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).formatToParts(date);
      const hour = parts.find((p) => p.type === 'hour');
      const minute = parts.find((p) => p.type === 'minute');
      const second = parts.find((p) => p.type === 'second');
      if (!hour || !minute || !second) return null;
      // Some engines render midnight as 24 rather than 00.
      return {
        hour: Number(hour.value) % 24,
        minute: Number(minute.value),
        second: Number(second.value),
      };
    } catch {
      // An unknown zone id on an old engine: skip the city rather than break the ticker.
      return null;
    }
  }

  /* Every city whose local hour is 2. Usually one to four, occasionally seven, never none. The zone travels with
     the name so the readout can be re-formatted every second without re-scanning the whole list. */
  function citiesAt2am(date) {
    const found = [];
    for (const [name, zone] of CITIES) {
      const time = localTime(zone, date);
      if (time && time.hour === 2) found.push({ name, zone });
    }
    return found;
  }

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  /* "2:14:07am" — the hour is always 2, which is the whole point. */
  function timeReadout(zone, date) {
    const time = localTime(zone, date);
    if (!time) return null;
    return `${time.hour}:${pad(time.minute)}:${pad(time.second)}am`;
  }

  /* ---------------------------------------------------------------------------
     Roller — one line of text that changes by rolling rather than blinking.

     Two lines share a clipped window: one is being read, the other waits above it. To swap, the waiting line is
     filled and both are re-stated — the reader drops out of the bottom, the waiting one falls into its place —
     and the roles trade for next time. Only text moves; the window's box never reflows what is beside it.

     `sizeToContent` is for the clock, where the mark and the divider sit either side of a line whose length
     changes with the city: the window animates to the incoming line's width so they settle once, with the text.
     The Ami hint leaves it off — it spans the input, so its width is not its own to set.
     --------------------------------------------------------------------------- */
  class Roller {
    constructor(windowEl, options) {
      this.windowEl = windowEl;
      this.lineEls = Array.from(windowEl.querySelectorAll('[data-roll-line]'));
      this.sizeToContent = Boolean(options && options.sizeToContent);
      this.rolling = false;
      this.timer = 0;

      const current = this.currentLine();
      if (this.sizeToContent && current) this.sizeTo(current);
    }

    currentLine() {
      return this.lineEls.find((el) => el.dataset.state === 'current') || null;
    }

    text() {
      const current = this.currentLine();
      return current ? current.textContent : '';
    }

    /* The window is absolute-positioned inside, so it has no width of its own: measure the line and set it. */
    sizeTo(lineEl) {
      const width = Math.ceil(lineEl.getBoundingClientRect().width);
      if (width > 0) this.windowEl.style.width = `${width}px`;
    }

    set(text, options) {
      if (this.lineEls.length < 2) return;

      const immediate = Boolean(options && options.immediate) || REDUCED_MOTION.matches;
      const currentEl = this.currentLine();
      const incomingEl = this.lineEls.find((el) => el !== currentEl);
      if (!currentEl || !incomingEl) return;
      if (currentEl.textContent === text) return;

      if (immediate) {
        window.clearTimeout(this.timer);
        this.rolling = false;
        currentEl.textContent = text;
        if (this.sizeToContent) this.sizeTo(currentEl);
        return;
      }

      // A roll already in flight owns both lines for its duration: filling the outgoing one would show text
      // mid-drop. The caller's next tick picks the change up.
      if (this.rolling) return;
      this.rolling = true;

      incomingEl.textContent = text;
      if (this.sizeToContent) this.sizeTo(incomingEl);

      // The incoming line is parked above with transitions off; it has to be laid out there before they are
      // re-enabled, or the browser coalesces both states and the line simply appears.
      void incomingEl.offsetHeight;

      currentEl.dataset.state = 'outgoing';
      incomingEl.dataset.state = 'current';

      // The line that just left becomes next turn's waiting line, once it is out of sight.
      this.timer = window.setTimeout(() => {
        currentEl.dataset.state = 'incoming';
        currentEl.textContent = '';
        this.rolling = false;
      }, ROLL_SETTLE_MS);
    }

    destroy() {
      window.clearTimeout(this.timer);
    }
  }

  class BottomRail extends HTMLElement {
    connectedCallback() {
      /* Never name an element reference `title`, `hidden`, `id` or `dir`: those shadow HTMLElement properties and
         the assignment silently becomes an attribute write (the <discover-hero> bug, assets/discover.js). */
      this.clockEl = this.querySelector('[data-rail-clock]');
      this.messagesEl = this.querySelector('[data-rail-messages]');
      this.messageEls = this.messagesEl
        ? Array.from(this.messagesEl.querySelectorAll('.bottom-rail__message'))
        : [];

      const clockWindowEl = this.querySelector('[data-rail-clock-window]');
      this.clockRoller = clockWindowEl ? new Roller(clockWindowEl, { sizeToContent: true }) : null;

      const seconds = Number(this.getAttribute('data-rotation-seconds'));
      this.rotationMs = (Number.isFinite(seconds) && seconds > 0 ? seconds : 8) * 1000;
      this.slotIndex = 0;
      this.cities = [];
      this.cityIndex = 0; // 0 = the resting phrase; cities are 1-based from here
      this.clockPhrase = this.getAttribute('data-clock-phrase') || 'always 2am somewhere';

      this.timeEl = this.querySelector('[data-rail-clock-time]');
      this.clockInEl = this.querySelector('[data-rail-clock-in]');

      this.setupAmi();
      this.reserveSpace();
      // First paint replaces the Liquid fallback ("It's 2am somewhere"): placed, not rolled in from nothing.
      this.refreshClock({ immediate: true });
      this.applyLayout();

      this.onCompactChange = () => this.applyLayout();
      if (typeof COMPACT.addEventListener === 'function') {
        COMPACT.addEventListener('change', this.onCompactChange);
      }

      this.onResize = () => this.reserveSpace();
      window.addEventListener('resize', this.onResize, { passive: true });

      /* Two clocks, deliberately different rates. The readout ticks every second and costs one Intl format of the
         city currently named. The set of cities in the 2am band only changes on the hour (and at :30 and :45,
         where the half- and quarter-hour zones come and go), so re-scanning all seventy every 15 seconds is well
         inside the resolution that matters and is the expensive call kept off the per-second path. */
      this.timeTimer = window.setInterval(() => this.renderTime(), 1000);
      this.clockTimer = window.setInterval(() => this.refreshClock(), 15000);

      /* The clock's window is sized by measuring the line, so a width measured against the fallback face sticks
         once the real face swaps in and the same string is a different length. Re-measure when fonts settle. */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (!this.isConnected || !this.clockRoller) return;
          const current = this.clockRoller.currentLine();
          if (current) this.clockRoller.sizeTo(current);
        });
      }
      this.startRotation();
      this.startAmiHint();
    }

    disconnectedCallback() {
      window.clearInterval(this.timeTimer);
      window.clearInterval(this.clockTimer);
      window.clearInterval(this.rotationTimer);
      window.clearTimeout(this.hintTimer);
      window.removeEventListener('resize', this.onResize);
      if (typeof COMPACT.removeEventListener === 'function') {
        COMPACT.removeEventListener('change', this.onCompactChange);
      }
      if (this.clockRoller) this.clockRoller.destroy();
      if (this.hintRoller) this.hintRoller.destroy();
    }

    /* The page reserves exactly the rail's height, so the footer's bottom bar is never covered. Measured rather
       than computed, because the compact breakpoint changes the capsule height. The row is measured, not the
       first capsule: the row is as tall as its tallest visible capsule, and below 1000px the first capsule in
       the markup (the ticker) is display: none and measures 0 — which reserved 24px under a 48px rail. */
    reserveSpace() {
      const row = this.querySelector('.bottom-rail__wrapper');
      if (!row) return;
      const styles = window.getComputedStyle(this);
      const padding =
        parseFloat(styles.paddingTop || '0') + parseFloat(styles.paddingBottom || '0');
      const reserve = Math.round(row.getBoundingClientRect().height + padding);
      document.documentElement.style.setProperty('--bottom-rail-reserve', `${reserve}px`);
      document.documentElement.style.setProperty('--bottom-rail-height', `${reserve}px`);
    }

    refreshClock(options) {
      const cities = citiesAt2am(new Date());
      if (cities.length === 0) {
        // Cannot happen with this list, but a stale phrase beats an empty capsule.
        return;
      }
      const countChanged = cities.length !== this.cities.length;
      this.cities = cities;
      if (this.cityIndex > cities.length) this.cityIndex = 0;
      this.renderClock(options);
      // The band changes on the hour: one city can become four, and a rail that never started cycling has to.
      if (countChanged) this.startRotation();
    }

    /* Only the place name rolls. The time beside it is a readout, so it ticks in place the way a clock does —
       rolling it every second would be a slot machine. It does jump when the city changes, which is correct:
       the half-hour zones are genuinely at a different minute (Kolkata 2:14 while Kathmandu is 2:29).

       Step 0 is the phrase: the roller carries "always 2am somewhere" after "It's", and the time and the "in"
       are hidden, so the line reads as the Liquid fallback does. */
    renderClock(options) {
      if (!this.clockRoller) return;
      const city = this.currentCity();
      if (!city) {
        this.clockRoller.set(this.clockPhrase, options);
        if (this.timeEl) this.timeEl.hidden = true;
        if (this.clockInEl) this.clockInEl.hidden = true;
        return;
      }
      this.clockRoller.set(city.name, options);
      this.renderTime();
    }

    currentCity() {
      if (this.cityIndex === 0 || this.cities.length === 0) return null;
      return this.cities[(this.cityIndex - 1) % this.cities.length] || null;
    }

    renderTime() {
      const city = this.currentCity();
      if (!this.timeEl || !city) return;
      const readout = timeReadout(city.zone, new Date());
      if (!readout) return;
      this.timeEl.textContent = readout;
      // "It's always 2am somewhere" becomes "It's 2:14:07am in <place>".
      this.timeEl.hidden = false;
      if (this.clockInEl) this.clockInEl.hidden = false;
    }

    /* Desktop: the clock is pinned to the left of the capsule and the messages roll beside it. Compact: the
       ticker capsule is display: none (bottom-rail.liquid), so there is nothing to drive — the rotation stops
       and the clock timers tick into a box nobody can see, which is cheap enough not to be worth a second
       lifecycle. Crossing back to desktop re-measures the clock window: a width measured while the capsule was
       display: none came back 0 and was skipped, so the roller's window has none until it is asked again. */
    applyLayout() {
      const hasMessages = this.messageEls.length > 0;

      if (this.messagesEl) this.messagesEl.hidden = !hasMessages;
      if (this.clockEl) this.clockEl.hidden = false;

      this.slotIndex = 0;
      this.showSlot(0);
      this.startRotation();
      this.reserveSpace();

      if (!COMPACT.matches && this.clockRoller) {
        const current = this.clockRoller.currentLine();
        if (current) this.clockRoller.sizeTo(current);
      }
    }

    /* The rotation runs over the messages; each slot indexes this.messageEls. */
    slots() {
      return this.messageEls.map((_, index) => index);
    }

    showSlot(position) {
      const slots = this.slots();
      if (slots.length === 0) return;
      const slot = slots[position % slots.length];

      /* Messages roll as whole elements rather than through a Roller, because each one is its own markup (a link,
         or plain text) rather than a string. The three states are the same ones the roller uses, so they read and
         animate identically: the one leaving drops out, the one arriving falls in from above. */
      this.messageEls.forEach((el, index) => {
        if (index === slot) {
          el.dataset.state = 'current';
        } else if (el.dataset.state === 'current') {
          el.dataset.state = 'outgoing';
          window.setTimeout(() => {
            if (el.dataset.state === 'outgoing') el.dataset.state = 'incoming';
          }, ROLL_SETTLE_MS);
        }
      });
    }

    startRotation() {
      window.clearInterval(this.rotationTimer);

      /* Auto-rotating text is moving content under WCAG 2.2.2, so a visitor who has asked for reduced motion gets
         a still rail: the first message, and the clock on the city it happened to land on. Compact has no ticker
         on screen to rotate; refreshClock calls back in here when the 2am band changes, so the guard lives here
         rather than in applyLayout. */
      if (REDUCED_MOTION.matches || COMPACT.matches) return;

      /* The clock always has something to cycle: the phrase and at least one city. Messages cycle beside it when
         there is more than one. */
      this.rotationTimer = window.setInterval(() => {
        if (this.slots().length > 1) {
          this.slotIndex = (this.slotIndex + 1) % this.slots().length;
          this.showSlot(this.slotIndex);
        }
        this.cityIndex = (this.cityIndex + 1) % (this.cities.length + 1);
        this.renderClock();
      }, this.rotationMs);
    }

    /* Ami: the capsule is the chat box. Submitting hands the question to <ami-chat> (assets/ami.js), which docks
       its card over this capsule (it reads [data-s2-ami-anchor]). The input is cleared on hand-off so the capsule
       is empty again behind the card. */
    setupAmi() {
      this.amiFormEl = this.querySelector('[data-rail-ami]');
      this.amiInputEl = this.querySelector('[data-rail-ami-input]');
      this.amiSendEl = this.querySelector('[data-rail-ami-send]');
      if (!this.amiFormEl) return;

      const hintEl = this.querySelector('[data-rail-ami-hint]');
      if (hintEl && this.amiInputEl) {
        this.hintEl = hintEl;
        this.hintRoller = new Roller(hintEl, { sizeToContent: false });
        /* The rolling hint replaces the input's own placeholder, which cannot be animated. The attribute stays in
           the markup so the capsule still reads as a chat box before this script lands, and is cleared here at
           the moment the hint takes over. */
        this.amiInputEl.removeAttribute('placeholder');

        this.amiInputEl.addEventListener('focus', () => this.syncAmiHint());
        this.amiInputEl.addEventListener('blur', () => this.syncAmiHint());
      }

      if (this.amiInputEl && this.amiSendEl) {
        this.amiInputEl.addEventListener('input', () => {
          this.syncAmiButton();
          this.syncAmiHint();
        });
        /* The open funnel's second step (plan §4.10): the field taken, once per page load. */
        this.amiInputEl.addEventListener(
          'focus',
          () => {
            if (!this.hasAttribute('data-ami-design-mode')) track('Ami Capsule Focused', { surface: 'rail' });
          },
          { once: true }
        );
      }
      this.syncAmiButton();
      /* The funnel's first step: the capsule is on screen (it always is once this element connects). Never
         from the theme editor, which the chat itself also keeps out of the loop. */
      if (!this.hasAttribute('data-ami-design-mode')) track('Ami Capsule Shown', { surface: 'rail' });

      this.amiFormEl.addEventListener('submit', (event) => {
        // Never a real navigation: the form element exists so Enter submits and assistive tech reads it as one.
        event.preventDefault();
        const question = this.amiInputEl ? this.amiInputEl.value.trim() : '';
        this.openAmi(question);
      });

      /* Compact: the whole capsule is the tap target and the input does not take the pointer
         (bottom-rail.liquid), so a tap on the field, the mark or the pill itself lands here and opens the chat —
         full-screen at this width, with its own composer, so there is nothing to type into here first. Taps on
         the button are left to its own submit, or one tap would open once and track twice. */
      this.amiFormEl.addEventListener('click', (event) => {
        if (!COMPACT.matches) return;
        const target = event.target instanceof Element ? event.target : null;
        if (target && target.closest('[data-rail-ami-send]')) return;
        this.openAmi('');
      });
    }

    /* Empty field: the button reopens the chat on whatever conversation is already there. Anything typed: it
       sends. The CSS swaps the icon off data-has-text; the label has to be swapped here because a screen reader
       cannot see which of the two icons is displayed. */
    syncAmiButton() {
      if (!this.amiFormEl || !this.amiInputEl || !this.amiSendEl) return;
      const hasText = this.amiInputEl.value.trim().length > 0;
      this.amiFormEl.setAttribute('data-has-text', hasText ? 'true' : 'false');
      this.amiSendEl.setAttribute('aria-label', hasText ? 'Send to Ami' : 'Open chat with Ami');
    }

    /* The hint is only ever shown over an empty, unfocused input: once someone is typing, or has typed, the field
       is theirs. */
    syncAmiHint() {
      if (!this.hintEl || !this.amiInputEl) return;
      const empty = this.amiInputEl.value.length === 0;
      const focused = document.activeElement === this.amiInputEl;
      this.hintEl.hidden = !empty || focused;
    }

    /* The default line is the resting state and holds longest; the suggestions pass through between. Read from
       the section's settings via data attributes, so the copy is editable without touching this file. */
    amiHintLines() {
      const defaultLine = this.getAttribute('data-ami-default') || 'Ask Ami anything';
      const suggestions = (this.getAttribute('data-ami-suggestions') || '')
        .split('|')
        .map((line) => line.trim())
        .filter(Boolean);

      const suggestionHold = this.rotationMs / 2;
      const restingHold = this.rotationMs * 1.25;

      const lines = [{ text: defaultLine, hold: restingHold }];
      for (const text of suggestions) lines.push({ text, hold: suggestionHold });
      return lines;
    }

    startAmiHint() {
      window.clearTimeout(this.hintTimer);
      if (!this.hintRoller) return;

      this.syncAmiHint();

      const lines = this.amiHintLines();
      // Only the default line: nothing to cycle, and it is already in the markup.
      if (lines.length <= 1 || REDUCED_MOTION.matches) return;

      this.hintIndex = 0;
      const step = () => {
        this.hintIndex = (this.hintIndex + 1) % lines.length;
        const line = lines[this.hintIndex];
        this.hintRoller.set(line.text);
        this.hintTimer = window.setTimeout(step, line.hold);
      };
      this.hintTimer = window.setTimeout(step, lines[0].hold);
    }

    /* The element reports the open itself ("Ami Opened" with surface: rail, plan §4.10), so nothing is
       tracked here. */
    openAmi(question) {
      if (window.AmiChat) {
        window.AmiChat.open(question || '', { surface: 'rail' });
      } else {
        // ami.js has not landed yet (it is deferred): the help centre is the honest fallback.
        const url = question
          ? `https://help.studio2am.co/?q=${encodeURIComponent(question.slice(0, 500))}`
          : 'https://help.studio2am.co';
        window.open(url, '_blank', 'noopener');
      }

      if (this.amiInputEl) {
        this.amiInputEl.value = '';
        this.amiInputEl.blur();
      }
      this.syncAmiButton();
      this.syncAmiHint();
    }
  }

  if (!window.customElements.get('bottom-rail')) {
    window.customElements.define('bottom-rail', BottomRail);
  }
})();
